import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AuthTemplate } from '@/components/auth/auth-template';
import { LoginForm } from '@/features/auth/login-form';
import { authStore } from '@/lib/auth-store';
import { goToPortalReturn, isAbsolutePortalReturn, safePortalReturn } from '@/lib/navigation';
import { effectiveReturn } from '@/lib/platform-lock';

function LoginPage() {
  const search = Route.useSearch();
  const returnTo = effectiveReturn(search.returnTo);
  const navigate = useNavigate();
  // Read once: after a sign-in through the form, LoginForm navigates itself.
  const [signedInOnLoad] = useState(() => authStore.isAuthenticated());
  // A refused locked sign-in (unified-access.md section 3.2) ends the
  // session and resolves with the reason; the form then shows it.
  const [resolving, setResolving] = useState(signedInOnLoad);

  useEffect(() => {
    if (!signedInOnLoad) return;
    const resolve = async () => {
      if (await goToPortalReturn(returnTo, navigate)) setResolving(false);
    };
    void resolve();
  }, [signedInOnLoad, returnTo, navigate]);

  if (resolving) return null;

  return (
    <AuthTemplate>
      <LoginForm returnTo={returnTo} />
    </AuthTemplate>
  );
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: safePortalReturn(search['returnTo']),
  }),
  /**
   * A platform whose own session ended sends the employee here with its page
   * as `returnTo` (ui-contract.md section 6). While the central session is
   * still valid, the employee goes straight back without typing the password
   * again; only a missing or expired central session shows the form. In
   * locked sign-in (unified-access.md section 3.2) the locked platform is the
   * return address and the portal is never the destination.
   */
  beforeLoad: async ({ search }) => {
    let signedIn = false;
    try {
      signedIn = await authStore.restore();
    } catch {
      // A failed check must not block signing in: show the form.
      return;
    }
    if (!signedIn) return;

    const returnTo = effectiveReturn(search.returnTo);
    if (!returnTo || !isAbsolutePortalReturn(returnTo)) throw redirect({ to: '/' });
    if (!authStore.getSession()?.selectedBranchId) {
      throw redirect({ to: '/select-branch', search: { returnTo } });
    }
    // LoginPage resolves the platform URL through POST /launch/return.
  },
  component: LoginPage,
});
